import React, { useState, useEffect } from "react";
import { FaEdit, FaTrash, FaChevronDown, FaSave } from "react-icons/fa";
import EditOrderAlert from "./EditOrderAlert";
import SaveDebtAlert from "./SaveDebtAlert";
import { setClientDebt } from "../utility/EndPointGoogleFirebase";

const DESKTOP_BREAKPOINT = 1024;

const OrderCard = ({
  clientName,
  orderData,
  onChange,
  onDelete,
  onEdit,
  readOnly,
  debt,
  onDebtChange,
}) => {
  const defaultOrderData = { default: 3, pane: "", note: "", varie: "", acconto: "" };
  const mergedOrderData = { ...defaultOrderData, ...orderData };

  const [isEditAlertVisible, setEditAlertVisible] = useState(false);
  const [isDebtAlertVisible, setDebtAlertVisible] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [isDesktop, setIsDesktop] = useState(false);
  const [debtInput, setDebtInput] = useState(debt ?? 0);
  const [debtSaving, setDebtSaving] = useState(false);
  const [debtSaved, setDebtSaved] = useState(false);
  const [fieldValues, setFieldValues] = useState({});

  useEffect(() => { setDebtInput(debt ?? 0); }, [debt]);

  useEffect(() => { setFieldValues({}); }, [orderData]);

  useEffect(() => {
    const check = () => setIsDesktop(window.innerWidth >= DESKTOP_BREAKPOINT);
    check();
    window.addEventListener("resize", check);
    return () => window.removeEventListener("resize", check);
  }, []);

  // Legge il valore di un campo come stringa, preservando "0" e valori intermedi come "0."
  const getRawValue = (fieldKey) => {
    if (fieldKey in fieldValues) return fieldValues[fieldKey];
    const v = mergedOrderData[fieldKey];
    return v === "" || v === undefined || v === null ? "" : String(v);
  };

  const handleEditClick = (e) => { e.stopPropagation(); setEditAlertVisible(true); };
  const closeEditAlert  = () => setEditAlertVisible(false);
  const confirmEditOrder = () => { onEdit(clientName); closeEditAlert(); };

  const handleDebtClick = (e) => { e.stopPropagation(); setDebtAlertVisible(true); };

  const handleSaveDebt = async () => {
    setDebtAlertVisible(false);
    try {
      setDebtSaving(true);
      const val = isNaN(parseFloat(debtInput)) ? 0 : parseFloat(debtInput);
      await setClientDebt(clientName, val);
      if (onDebtChange) onDebtChange(clientName, val);
      setDebtSaved(true);
      setTimeout(() => setDebtSaved(false), 2000);
    } catch (err) {
      console.error("Errore salvataggio debito:", err);
    } finally {
      setDebtSaving(false);
    }
  };

  const hasData  = mergedOrderData.pane || mergedOrderData.varie || mergedOrderData.acconto || mergedOrderData.note;
  const hasDebt  = parseFloat(debtInput) > 0;
  const cardState = readOnly ? "saved" : hasData ? "filled" : "empty";

  const stateStyles = {
    saved:  { borderColor: "#16a34a", background: "#ffffff", headerBg: "#f0fdf4", accent: "#16a34a", badgeColor: "#15803d", badgeText: "Salvato"   },
    filled: { borderColor: "#86efac", background: "#ffffff", headerBg: "#f0fdf4", accent: "#86efac", badgeColor: "#16a34a", badgeText: "Compilato"  },
    empty:  { borderColor: "#e5e7eb", background: "#ffffff", headerBg: "#ffffff", accent: "#e5e7eb", badgeColor: "#9ca3af", badgeText: "Vuoto"      },
  };

  const { borderColor, background, headerBg, accent, badgeColor, badgeText } = stateStyles[cardState];
  const initials = clientName.split(" ").map(w => w[0]).join("").toUpperCase().slice(0, 2);

  const fields = [
    { field: "Default", type: "number", step: "0.01" },
    { field: "Pane",    type: "number" },
    { field: "Varie",   type: "number" },
    { field: "Acconto", type: "number" },
  ];

  const showBody = isDesktop || isExpanded;

  const inputStyle = {
    display: "block", width: "100%",
    padding: "8px 10px", borderRadius: 7,
    border: "1px solid #e5e7eb", fontSize: 14,
    color: readOnly ? "#6b7280" : "#111827",
    background: readOnly ? "rgba(255,255,255,0.4)" : "rgba(255,255,255,0.8)",
    outline: "none", boxSizing: "border-box",
    WebkitAppearance: "none", appearance: "none",
    cursor: readOnly ? "not-allowed" : "text",
    fontFamily: "inherit",
  };

  const labelStyle = {
    display: "block", fontSize: 11, fontWeight: 500,
    color: "#9ca3af", letterSpacing: "0.06em",
    textTransform: "uppercase", marginBottom: 4,
  };

  const btnBase = {
    display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
    padding: "8px 14px", borderRadius: 8,
    fontSize: 12, fontWeight: 500, cursor: "pointer",
    WebkitTapHighlightColor: "transparent",
    fontFamily: "inherit", flex: 1,
  };

  return (
    <div style={{
      borderRadius: 12,
      border: `1.5px solid ${borderColor}`,
      background,
      boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
    }}>

      {/* ── Header ── */}
      <div
        onClick={() => { if (!isDesktop) setIsExpanded(v => !v); }}
        style={{
          display: "flex", alignItems: "center", gap: 10,
          padding: "12px 14px",
          cursor: isDesktop ? "default" : "pointer",
          userSelect: "none", WebkitUserSelect: "none",
          WebkitTapHighlightColor: "transparent",
          background: headerBg,
          borderRadius: isExpanded || isDesktop ? "10px 10px 0 0" : 10,
        }}
      >
        <div style={{
          width: 34, height: 34, borderRadius: "50%", flexShrink: 0,
          background: accent === "#e5e7eb" ? "#f3f4f6" : `${accent}22`,
          border: `1.5px solid ${accent}`,
          display: "flex", alignItems: "center", justifyContent: "center",
          fontSize: 11, fontWeight: 600,
          color: accent === "#e5e7eb" ? "#9ca3af" : accent,
        }}>
          {initials}
        </div>

        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontWeight: 500, fontSize: 14, color: "#111827", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", textTransform: "uppercase", letterSpacing: "0.03em" }}>
            {clientName}
          </div>
          <div style={{ fontSize: 11, color: badgeColor, marginTop: 1 }}>
            {badgeText}
          </div>
        </div>

        {!isDesktop && (
          <div style={{
            color: "#9ca3af", fontSize: 13, flexShrink: 0,
            transform: isExpanded ? "rotate(180deg)" : "rotate(0deg)",
            transition: "transform 0.25s ease",
            display: "flex", alignItems: "center", pointerEvents: "none",
          }}>
            <FaChevronDown />
          </div>
        )}
      </div>

      {/* ── Body ── */}
      {showBody && (
        <>
          <div style={{ height: 1, background: "#f3f4f6", margin: "0 14px" }} />

          <div style={{ padding: "12px 14px" }}>

            {/* Campi ordine */}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 10 }}>
              {fields.map(({ field, type, step }, index) => {
                const key = field.toLowerCase().replace(/\s+/g, "");
                return (
                  <div key={index}>
                    <label style={labelStyle}>{field}</label>
                    <input
                      style={inputStyle}
                      type={type}
                      inputMode="decimal"
                      step={step || "1"}
                      value={getRawValue(key)}
                      readOnly={readOnly}
                      onChange={(e) => {
                        const raw = e.target.value;
                        // Aggiorna lo stato locale per permettere digitazione libera (es. "0", "0.", "-")
                        setFieldValues(prev => ({ ...prev, [key]: raw }));
                        // Propaga al padre solo se il valore è vuoto o un numero valido
                        const num = raw === "" ? "" : parseFloat(raw);
                        if (raw === "" || !isNaN(num)) {
                          onChange(clientName, key, raw === "" ? "" : num);
                        }
                      }}
                    />
                  </div>
                );
              })}

              {/* Note */}
              <div style={{ gridColumn: "1 / -1" }}>
                <label style={labelStyle}>Note</label>
                <input
                  style={inputStyle}
                  type="text"
                  value={getRawValue("note")}
                  readOnly={readOnly}
                  onChange={(e) => {
                    const raw = e.target.value;
                    setFieldValues(prev => ({ ...prev, note: raw }));
                    onChange(clientName, "note", raw);
                  }}
                />
              </div>

              {/* Saldo precedente */}
              <div style={{ gridColumn: "1 / -1" }}>
                <label style={{ ...labelStyle, color: hasDebt ? "#dc2626" : "#9ca3af" }}>
                  Saldo precedente
                </label>
                <input
                  style={{
                    ...inputStyle,
                    background: "rgba(255,255,255,0.8)",
                    border: `1px solid ${hasDebt ? "#fca5a5" : "#e5e7eb"}`,
                    color: hasDebt ? "#dc2626" : "#6b7280",
                    fontWeight: hasDebt ? 600 : 400,
                    cursor: "text",
                  }}
                  type="number"
                  inputMode="decimal"
                  step="0.01"
                  placeholder="0.00"
                  value={debtInput}
                  onChange={(e) => setDebtInput(e.target.value)}
                />
              </div>
            </div>

            {/* Bottoni */}
            <div style={{ margin: "12px -14px 0", borderTop: "1px solid #e5e7eb", padding: "12px 14px 0", background: "#fff" }}>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>

                <button
                  onClick={handleEditClick}
                  style={{ ...btnBase, background: "#eef2ff", color: "#4338ca", border: "1px solid #c7d2fe" }}
                >
                  <FaEdit style={{ fontSize: 13 }} /> Modifica
                </button>

                <button
                  onClick={handleDebtClick}
                  disabled={debtSaving}
                  style={{
                    ...btnBase,
                    background: debtSaved ? "#f0fdf4" : "#fff1f2",
                    color: debtSaved ? "#15803d" : "#dc2626",
                    border: `1px solid ${debtSaved ? "#bbf7d0" : "#fca5a5"}`,
                    opacity: debtSaving ? 0.6 : 1,
                    transition: "all 0.2s",
                  }}
                >
                  <FaSave style={{ fontSize: 13 }} />
                  {debtSaving ? "..." : debtSaved ? "Salvato!" : "Debito"}
                </button>

                <button
                  onClick={(e) => { e.stopPropagation(); onDelete(clientName); }}
                  style={{ ...btnBase, background: "#f9fafb", color: "#6b7280", border: "1px solid #e5e7eb", flex: "0 0 auto", padding: "8px 12px" }}
                >
                  <FaTrash style={{ fontSize: 13 }} />
                </button>

              </div>
            </div>
          </div>
        </>
      )}

      <EditOrderAlert
        isVisible={isEditAlertVisible}
        onCancel={closeEditAlert}
        onConfirm={confirmEditOrder}
        clientName={clientName}
      />
      <SaveDebtAlert
        isVisible={isDebtAlertVisible}
        onCancel={() => setDebtAlertVisible(false)}
        onConfirm={handleSaveDebt}
        clientName={clientName}
        debtValue={debtInput}
      />
    </div>
  );
};

export default OrderCard;